package com.gloopgo.game.engine

/**
 * Constants and balancing parameters for GloopGo.
 */
object GameConstants {
    // App Metadata
    const val APP_VERSION = "1.0.6"

    // World dimensions
    const val WORLD_WIDTH = 12000f
    const val WORLD_HEIGHT = 12000f
    const val GRID_SIZE = 100f
    const val DEFAULT_ROOM_ID = "arena-main"

    // Player Name Constraints
    const val MIN_PLAYER_NAME_LENGTH = 4
    const val MAX_PLAYER_NAME_LENGTH = 25

    // Food Pellets
    const val PELLET_COUNT = 600
    const val PELLET_MASS = 1f

    const val PELLET_BASE_RADIUS = 7f
    const val PELLET_RADIUS_MASS_SCALE = 1.1f

    val FOOD_EMOJIS = listOf(
        "🍕", "🍔", "🍟", "🌭", "🍿", "🍩", "🍪", "🎂", "🍰", "🧁",
        "🍦", "🍨", "🍧", "🍫", "🍬", "🍭", "🍓", "🍒", "🍎", "🍉",
        "🍇", "🍌", "🍍", "🍑", "🥑", "🌮", "🌯", "🍣", "🥟", "🥞", "🧇"
    )

    fun getFoodEmojiForId(id: String): String {
        val index = kotlin.math.abs(id.hashCode()) % FOOD_EMOJIS.size
        return FOOD_EMOJIS[index]
    }

    // Hazard Pellets (Poop & Bomb)
    const val HAZARD_PELLET_MASS = -10f
    const val HAZARD_PELLET_RADIUS = 16f
    const val HAZARD_SPAWN_CHANCE = 0.12f // ~12% of spawned pellets are hazards
    val HAZARD_EMOJIS = listOf("💩", "💣")
    const val HAZARD_COLOR_POOP = 0xFF5D4037L // Earthy brown
    const val HAZARD_COLOR_BOMB = 0xFF212121L // Charcoal danger black

    fun isHazardEmoji(emoji: String): Boolean = emoji == "💩" || emoji == "💣"

    // Mass & Physics
    const val INITIAL_MASS = 20f

    const val MASS_TO_RADIUS_FACTOR = 76.5f // radius = sqrt(mass * factor) (3x larger radius: 8.5 * 9)
    const val BASE_SPEED = 1200f // Base speed for logarithmic speed calculation
    const val SPEED_LOG_OFFSET = 1.0f // Logarithmic offset: speed = BASE_SPEED / (SPEED_LOG_OFFSET + ln(mass))
    const val MIN_SPEED = 105f // 3x faster minimum speed clamp (35 * 3)

    const val EAT_MASS_ADVANTAGE = 1.15f // Must be 15% larger in mass to eat another cell

    // Splitting
    const val SPLIT_MIN_MASS = 36f
    const val SPLIT_IMPULSE_SPEED = 1950f // 3x faster split impulse (650 * 3)
    const val SPLIT_DECAY = 4.5f
    const val SPLIT_COOLDOWN_MS = 10_000L // 10 seconds before split cells can merge
    const val SPLIT_ACTION_COOLDOWN_MS = 1000L // 1 second cooldown between split actions
    const val MAX_CELLS_PER_PLAYER = 16

    // Eject Mass
    const val EJECT_MIN_MASS = 35f
    const val EJECTED_MASS_VALUE = 12f
    const val EJECT_IMPULSE_SPEED = 1440f // 3x faster eject impulse (480 * 3)
    const val EJECT_DECAY = 5.0f
    const val EJECT_ACTION_COOLDOWN_MS = 1000L // 1 second cooldown between feed/eject actions


    // Camera
    const val BASE_ZOOM = 1.0f
    const val ZOOM_EXPONENT = 0.35f
    const val MIN_ZOOM = 0.05f
    const val MAX_ZOOM = 3.5f

    // Disconnect / Heartbeat timeout
    const val HEARTBEAT_TIMEOUT_MS = 5000L

    // Palette for blobs and pellets
    val VIBRANT_COLORS = listOf(
        0xFF00E5FF, // Cyan
        0xFFFF1744, // Bright Red
        0xFF00E676, // Bright Green
        0xFFFFD600, // Bright Yellow
        0xFFFF4081, // Pink
        0xFF7C4DFF, // Purple
        0xFFFF9100, // Orange
        0xFF00B0FF, // Light Blue
        0xFF69F0AE, // Mint Green
        0xFFF50057  // Rose
    )
}
